/**
 * Authentication Provider (Client-Side)
 *
 * Provides centralized authentication state management using Supabase JS client.
 * Uses onAuthStateChange listener for session management.
 *
 * Key differences from Next.js version:
 * - Uses @supabase/supabase-js directly (not @supabase/ssr)
 * - Environment variables use VITE_ prefix
 * - No server-side session handling
 */

import { createApiClient } from '@cortex/shared/api-client';
import type {
  AuthChangeEvent,
  Session,
  User as SupabaseUser,
} from '@supabase/supabase-js';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { supabase } from '@/lib/supabase';
import { useAuthStore, type User } from '@/store/auth-store';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/**
 * Maps Supabase user to our store user format (fallback when API unavailable)
 */
function mapSupabaseUserToStoreUser(supabaseUser: SupabaseUser): User {
  const name = supabaseUser.user_metadata?.['full_name'] as string | undefined;
  const avatarUrl = supabaseUser.user_metadata?.['avatar_url'] as
    | string
    | undefined;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email ?? '',
    ...(name !== undefined && { name }),
    ...(avatarUrl !== undefined && { avatarUrl }),
    role: 'member',
  };
}

/**
 * Fetches full user profile from the API (includes role, organization).
 * Falls back to basic Supabase user data if the API is unreachable.
 */
async function fetchAndSetUserProfile(session: Session): Promise<void> {
  try {
    const api = createApiClient(API_BASE_URL, {
      headers: session.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {},
    });
    const response = await api.users.index.$get();
    if (response.ok) {
      const json = await response.json();
      if ('data' in json) {
        const profile = json.data;
        useAuthStore.getState().setUser({
          id: profile.id,
          email: profile.email,
          role: profile.role === 'admin' ? 'admin' : 'member',
          ...(profile.fullName && { name: profile.fullName }),
          ...(profile.avatarUrl && { avatarUrl: profile.avatarUrl }),
          ...(profile.organization && { organization: profile.organization }),
        });
        return;
      }
    }
  } catch {
    // API unreachable — fall through to fallback
  }

  // Fallback to basic Supabase user data
  const storeUser = mapSupabaseUserToStoreUser(session.user);
  useAuthStore.getState().setUser(storeUser);
}

/**
 * Authentication context shape
 */
export interface AuthContextValue {
  /** Current authenticated user */
  user: User | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth is initializing (session check in progress) */
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application to provide authentication state and
 * automatically sync Supabase session changes with the auth store.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to Zustand store for auth state
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    // Get initial session and fetch full profile (including role)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        await fetchAndSetUserProfile(session);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        switch (event) {
          case 'SIGNED_IN':
          case 'TOKEN_REFRESHED':
          case 'USER_UPDATED':
            if (session) {
              await fetchAndSetUserProfile(session);
            }
            break;

          case 'SIGNED_OUT':
            useAuthStore.getState().logout();
            break;

          case 'PASSWORD_RECOVERY':
            // Password recovery handled by dedicated page/flow
            break;

          default:
            break;
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 *
 * Must be used within an AuthProvider tree.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
}
